// Library Methods
import { useTranslation } from "react-i18next";

// Components
import Box from "@mui/material/Box";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import OperatingManualContent from "../OperatingManualContent/OperatingManualContent";

import headsetImg from "src/assets/images/operating-manual/getting-started/headset.png";
import controllerImg from "src/assets/images/operating-manual/getting-started/controller.png";
import powerButtonImg from "src/assets/images/operating-manual/getting-started/power-button.png";
import usbcPortImg from "src/assets/images/operating-manual/getting-started/usbc-port.png";
import volumeImg from "src/assets/images/operating-manual/getting-started/volume.png";
import picoGreenImg from "src/assets/images/operating-manual/getting-started/pico-green.png";
import picoBlueImg from "src/assets/images/operating-manual/getting-started/pico-blue.png";
import picoRedImg from "src/assets/images/operating-manual/getting-started/pico-red.png";
import picoOrangeImg from "src/assets/images/operating-manual/getting-started/pico-orange.png";
import picoBlueFlashingImg from "src/assets/images/operating-manual/getting-started/pico-blue-flashing.png";
import picoRedFlashingImg from "src/assets/images/operating-manual/getting-started/pico-red-flashing.png";
import picoBlueRedImg from "src/assets/images/operating-manual/getting-started/pico-blue-red.png";
import picoOffImg from "src/assets/images/operating-manual/getting-started/pico-off.png";

import selectButtonImg from "src/assets/images/operating-manual/getting-started/select-button.png";
import pauseButtonImg from "src/assets/images/operating-manual/getting-started/pause-button.png";
import recenterButtonImg from "src/assets/images/operating-manual/getting-started/recenter-button.png";
import powerAdminButtonImg from "src/assets/images/operating-manual/getting-started/power-admin-settings-button.png";
import triggerImg from "src/assets/images/operating-manual/getting-started/trigger.png";

type HeadMountedDeviceControlsType = {
  title: string;
};

const HeadMountedDeviceControls = ({
  title,
}: HeadMountedDeviceControlsType) => {
  const { t } = useTranslation("operating-manual");

  return (
    <OperatingManualContent title={title}>
      <Box sx={{ display: "flex", flexDirection: "column", gap: "20px" }}>
        <Box sx={{ mx: "20px" }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: "20px",
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <img src={headsetImg} width="70%" alt="headset" />
            </Box>

            <Box
              id="headset-buttons"
              sx={{
                padding: "20px",
                borderRadius: "8px",
                backgroundColor: "#5B6D7A30",
              }}
            >
              <Box
                sx={{ display: "flex", justifyContent: "center", mb: "20px" }}
              >
                <Typography fontWeight={600}>{t("Headset Buttons")}</Typography>
              </Box>

              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "20px" }}
              >
                <Grid container sx={{ display: "flex", alignItems: "center" }}>
                  <Grid item xs={2}>
                    <img src={powerButtonImg} width="65%" alt="power-button" />
                  </Grid>
                  <Grid item xs={10}>
                    <Typography fontSize="14px" fontWeight={600}>
                      1. {t("Power button")}
                    </Typography>
                    <Typography fontSize="14px">
                      {t(
                        "Short press to turn on. Long press for 6 seconds to turn off."
                      )}
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container sx={{ display: "flex", alignItems: "center" }}>
                  <Grid item xs={2}>
                    <img src={usbcPortImg} width="65%" alt="usbc-port" />
                  </Grid>
                  <Grid item xs={10}>
                    <Typography fontSize="14px" fontWeight={600}>
                      2. {t("USB-C port")}
                    </Typography>
                    <Typography fontSize="14px">
                      {t("Charging port for the RetinaLogik-provided charger.")}
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container sx={{ display: "flex", alignItems: "center" }}>
                  <Grid item xs={2}>
                    <img src={volumeImg} width="65%" alt="volume" />
                  </Grid>
                  <Grid item xs={10}>
                    <Typography fontSize="14px" fontWeight={600}>
                      3. {t("Volume")}
                    </Typography>
                    <Typography fontSize="14px">
                      {t(
                        "Press the + button to increase volume. Press the - button to decrease volume."
                      )}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Box>

            <Box
              id="headset-status-indicators"
              sx={{
                padding: "20px",
                borderRadius: "8px",
                backgroundColor: "#5B6D7A30",
              }}
            >
              <Box
                sx={{ display: "flex", justifyContent: "center", mb: "20px" }}
              >
                <Typography fontWeight={600}>
                  {t("Headset Status Indicators")}
                </Typography>
              </Box>

              <Grid container rowGap="8px">
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "20px",
                    }}
                  >
                    <Grid
                      container
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        columnGap: "20px",
                      }}
                    >
                      <img
                        src={picoGreenImg}
                        width="20px"
                        height="20px"
                        alt="pico-green"
                      />
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          maxWidth: "80%",
                        }}
                      >
                        <Typography fontSize="14px" fontWeight={600}>
                          {t("Green")}
                        </Typography>
                        <Typography fontSize="14px">
                          {t("Charging and battery is more than 98% charged")}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid
                      container
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        columnGap: "20px",
                      }}
                    >
                      <img
                        src={picoBlueImg}
                        width="20px"
                        height="20px"
                        alt="pico-blue"
                      />
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          maxWidth: "80%",
                        }}
                      >
                        <Typography fontSize="14px" fontWeight={600}>
                          {t("Blue")}
                        </Typography>
                        <Typography fontSize="14px">
                          {t("Powered on with over 20% battery")}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid
                      container
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        columnGap: "20px",
                      }}
                    >
                      <img
                        src={picoRedImg}
                        width="20px"
                        height="20px"
                        alt="pico-red"
                      />
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          maxWidth: "80%",
                        }}
                      >
                        <Typography fontSize="14px" fontWeight={600}>
                          {t("Red")}
                        </Typography>
                        <Typography fontSize="14px">
                          {t("Powered on with less than 20% battery")}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid
                      container
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        columnGap: "20px",
                      }}
                    >
                      <img
                        src={picoOrangeImg}
                        width="20px"
                        height="20px"
                        alt="pico-orange"
                      />
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          maxWidth: "80%",
                        }}
                      >
                        <Typography fontSize="14px" fontWeight={600}>
                          {t("Orange")}
                        </Typography>
                        <Typography fontSize="14px">
                          {t("Charging and battery is less than 98% charged")}
                        </Typography>
                      </Box>
                    </Grid>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "20px",
                    }}
                  >
                    <Grid
                      container
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        columnGap: "20px",
                      }}
                    >
                      <img
                        src={picoBlueFlashingImg}
                        width="20px"
                        height="20px"
                        alt="pico-blue-flashing"
                      />
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          maxWidth: "80%",
                        }}
                      >
                        <Typography fontSize="14px" fontWeight={600}>
                          {t("Flashing Blue")}
                        </Typography>
                        <Typography fontSize="14px">
                          {t("Turning on / off")}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid
                      container
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        columnGap: "20px",
                      }}
                    >
                      <img
                        src={picoRedFlashingImg}
                        width="20px"
                        height="20px"
                        alt="pico-red-flashing"
                      />
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          maxWidth: "80%",
                        }}
                      >
                        <Typography fontSize="14px" fontWeight={600}>
                          {t("Flashing Red")}
                        </Typography>
                        <Typography fontSize="14px">
                          {t("Battery less than 20%")}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid
                      container
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        columnGap: "20px",
                      }}
                    >
                      <img
                        src={picoOffImg}
                        width="20px"
                        height="20px"
                        alt="pico-off"
                      />
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          maxWidth: "80%",
                        }}
                      >
                        <Typography fontSize="14px" fontWeight={600}>
                          {t("Off")}
                        </Typography>
                        <Typography fontSize="14px">
                          {t("Sleeping or powered off")}
                        </Typography>
                      </Box>
                    </Grid>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            <Box
              sx={{
                display: "flex",
                justifyContent: "center",
              }}
            >
              <img src={controllerImg} width="60%" alt="controller" />
            </Box>

            <Box
              id="controller-buttons"
              sx={{
                padding: "20px",
                borderRadius: "8px",
                backgroundColor: "#5B6D7A30",
              }}
            >
              <Box
                sx={{ display: "flex", justifyContent: "center", mb: "20px" }}
              >
                <Typography fontWeight={600}>
                  {t("Controller Buttons")}
                </Typography>
              </Box>

              <Box
                sx={{ display: "flex", flexDirection: "column", gap: "20px" }}
              >
                <Grid container sx={{ display: "flex", alignItems: "center" }}>
                  <Grid item xs={2}>
                    <img
                      src={selectButtonImg}
                      width="65%"
                      alt="select-button"
                    />
                  </Grid>
                  <Grid item xs={10}>
                    <Typography fontSize="14px" fontWeight={600}>
                      1. {t("Select")}
                    </Typography>
                    <Typography fontSize="14px">
                      {t(
                        "Select button. Right controller has A, left controller has X."
                      )}
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container sx={{ display: "flex", alignItems: "center" }}>
                  <Grid item xs={2}>
                    <img src={pauseButtonImg} width="65%" alt="pause-button" />
                  </Grid>
                  <Grid item xs={10}>
                    <Typography fontSize="14px" fontWeight={600}>
                      2. {t("Pause")}
                    </Typography>
                    <Typography fontSize="14px">
                      {t(
                        "Pause exam. Right controller has B, left controller has Y."
                      )}
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container sx={{ display: "flex", alignItems: "center" }}>
                  <Grid item xs={2}>
                    <img
                      src={recenterButtonImg}
                      width="65%"
                      alt="recenter-button"
                    />
                  </Grid>
                  <Grid item xs={10}>
                    <Typography fontSize="14px" fontWeight={600}>
                      3. {t("Recenter")}
                    </Typography>
                    <Typography fontSize="14px">
                      {t("Press this button once to recenter the scene.")}
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container sx={{ display: "flex", alignItems: "center" }}>
                  <Grid item xs={2}>
                    <img
                      src={powerAdminButtonImg}
                      width="65%"
                      alt="power-admin-button"
                    />
                  </Grid>
                  <Grid item xs={10}>
                    <Typography fontSize="14px" fontWeight={600}>
                      4. {t("Power / Admin Settings")}
                    </Typography>
                    <Typography fontSize="14px">
                      {t(
                        "Short press to turn on. Long press for 6 seconds to turn off. Short press while the controller is on to activate the admin menu."
                      )}
                    </Typography>
                  </Grid>
                </Grid>

                <Grid container sx={{ display: "flex", alignItems: "center" }}>
                  <Grid item xs={2}>
                    <img src={triggerImg} width="65%" alt="trigger-button" />
                  </Grid>
                  <Grid item xs={10}>
                    <Typography fontSize="14px" fontWeight={600}>
                      5. {t("Select")}
                    </Typography>
                    <Typography fontSize="14px">
                      {t(
                        "Patients can use this to indicate they see a stimulus, and will use this button to load and launch the exam."
                      )}
                    </Typography>
                  </Grid>
                </Grid>
              </Box>
            </Box>

            <Box
              id="controller-status-indicators"
              sx={{
                padding: "20px",
                borderRadius: "8px",
                backgroundColor: "#5B6D7A30",
              }}
            >
              <Box
                sx={{ display: "flex", justifyContent: "center", mb: "20px" }}
              >
                <Typography fontWeight={600}>
                  {t("Controller Status Indicators")}
                </Typography>
              </Box>

              <Grid container rowGap="8px">
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "20px",
                    }}
                  >
                    <Grid
                      container
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        columnGap: "20px",
                      }}
                    >
                      <img
                        src={picoBlueFlashingImg}
                        width="20px"
                        height="20px"
                        alt="pico-blue-flashing"
                      />
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          maxWidth: "80%",
                        }}
                      >
                        <Typography fontSize="14px" fontWeight={600}>
                          {t("Flashing Blue")}
                        </Typography>
                        <Typography fontSize="14px">
                          {t("Turning on or searching for connection")}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid
                      container
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        columnGap: "20px",
                      }}
                    >
                      <img
                        src={picoBlueImg}
                        width="20px"
                        height="20px"
                        alt="pico-blue"
                      />
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          maxWidth: "80%",
                        }}
                      >
                        <Typography fontSize="14px" fontWeight={600}>
                          {t("Blue")}
                        </Typography>
                        <Typography fontSize="14px">
                          {t("Update in progress")}
                        </Typography>
                      </Box>
                    </Grid>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: "20px",
                    }}
                  >
                    <Grid
                      container
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        columnGap: "20px",
                      }}
                    >
                      <img
                        src={picoBlueRedImg}
                        width="20px"
                        height="20px"
                        alt="pico-blue-red"
                      />
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          maxWidth: "80%",
                        }}
                      >
                        <Typography fontSize="14px" fontWeight={600}>
                          {t("Alternating Blue and Red")}
                        </Typography>
                        <Typography fontSize="14px">
                          {t("Pairing or unpairing with headset")}
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid
                      container
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        columnGap: "20px",
                      }}
                    >
                      <img
                        src={picoOffImg}
                        width="20px"
                        height="20px"
                        alt="pico-off"
                      />
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          maxWidth: "80%",
                        }}
                      >
                        <Typography fontSize="14px" fontWeight={600}>
                          {t("Off")}
                        </Typography>
                        <Typography fontSize="14px">
                          {t("Connected or turned off")}
                        </Typography>
                      </Box>
                    </Grid>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </Box>
      </Box>
    </OperatingManualContent>
  );
};

export default HeadMountedDeviceControls;
